// Keeps a chat's checked table rows available to the composer and freezes
// their references into each outgoing message.
import { createContext } from 'react';

import {
    draftIssue,
    MESSAGE_CHARACTER_LIMIT,
} from '@apps/web/features/phoebe_chat_v3/model/draft_rules';
import { type UiContextSnapshot } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import { selectionActionMessage } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { type SelectableIdColumn } from '@apps/web/features/phoebe_chat_v3/registry/types';

export type TableSelection = Readonly<{
    readonly eventId: string;
    readonly idColumn: SelectableIdColumn;
    readonly ids: ReadonlySet<string>;
    readonly receipt?: UiContextSnapshot['receipt'];
}>;

// One active table per chat matches the server's single-card context contract.
// Keeping selection above the transcript also preserves it during virtualization.
export function createTableSelectionStore() {
    let selection: TableSelection | null = null;
    let revision = 0;
    const listeners = new Set<() => void>();
    return {
        getSnapshot: () => selection,
        getRevision: () => revision,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        update: (next: TableSelection | null) => {
            selection = next;
            revision += 1;
            for (const listener of listeners) {
                listener();
            }
        },
    };
}

export const TableSelectionContext = createContext<ReturnType<
    typeof createTableSelectionStore
> | null>(null);

export function messageWithTableSelection(
    content: string,
    selection: TableSelection | null,
): Readonly<{
    readonly content: string;
    readonly ui_context?: UiContextSnapshot;
}> {
    if (selection === null || selection.ids.size === 0) {
        return { content };
    }
    const ids = Array.from(selection.ids);
    // Persist visible links in the message, just like calendar attachments.
    // Table actions and @mentions may already contain some of these links.
    const contentWithReferences = selectionActionMessage(
        content,
        selection.idColumn,
        ids.filter(
            (id) =>
                !content.includes(
                    selectionActionMessage('', selection.idColumn, [id]).trim(),
                ),
        ),
    );
    // Older receipts and mixed-entity tables cannot be verified as one card.
    // Explicit references still convey every selected ID without truncation.
    if (selection.receipt === undefined || ids.length > 200) {
        return {
            content: contentWithReferences,
        };
    }
    return {
        content: contentWithReferences,
        ui_context: {
            receipt: selection.receipt,
            reference: 'selected_rows',
            visible_row_ids: ids,
        },
    };
}

// Prepare and consume one selection snapshot for either message endpoint.
// A null result rejects locally, before the composer discards its draft.
export function submitTableSelection({
    content,
    store,
    send,
    onTooLong,
    restoreOnFailure = false,
}: Readonly<{
    content: string;
    store: ReturnType<typeof createTableSelectionStore>;
    send: (
        message: ReturnType<typeof messageWithTableSelection>,
    ) => Promise<boolean>;
    onTooLong: () => void;
    restoreOnFailure?: boolean;
}>): Promise<boolean> | null {
    const selection = store.getSnapshot();
    const message = messageWithTableSelection(content, selection);
    if (
        draftIssue(message.content, {
            limit: MESSAGE_CHARACTER_LIMIT,
            required: false,
        }) !== null
    ) {
        onTooLong();
        return null;
    }
    // Retries use the prepared request; the next draft starts unselected.
    store.update(null);
    const clearedRevision = store.getRevision();
    return send(message).then((accepted) => {
        // Answers have no optimistic retry row. Restore only untouched state,
        // including when the user selected new rows and explicitly cleared them.
        if (
            !accepted &&
            restoreOnFailure &&
            store.getRevision() === clearedRevision
        ) {
            store.update(selection);
        }
        return accepted;
    });
}
